import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format, startOfDay, startOfMonth } from 'date-fns';
import {
  buildStorefrontCalendarAvailabilityMap,
  isStorefrontDayOpen,
} from './buildStorefrontCalendarAvailabilityMap';
import { computeStorefrontTotalDurationMinutes } from '../prefetchStorefrontAvailability';

function buildAvailabilityRequestKey({
  companyId,
  serviceId,
  packageId,
  addonIdsKey,
  totalDuration,
  locationId,
  customerNoStaffMessage,
  monthKey,
  minDateKey,
  maxDateKey,
  companyHoursKey,
}) {
  return [
    companyId,
    serviceId,
    packageId,
    addonIdsKey,
    totalDuration,
    locationId || '',
    customerNoStaffMessage || '',
    monthKey,
    minDateKey,
    maxDateKey,
    companyHoursKey,
  ].join('|');
}

export function useStorefrontCalendarAvailability({
  enabled = true,
  companyData,
  service,
  selectedPackage = null,
  selectedAddons = [],
  companyHours = {},
  locationId = null,
  location = null,
  customerNoStaffMessage,
  monthCursor,
  minDate,
  maxDate,
}) {
  const [availabilityByDate, setAvailabilityByDate] = useState(() => new Map());
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  const companyId = companyData?.id;
  const serviceId = service?.id;
  const packageId = selectedPackage?.id ?? '';
  const addonIdsKey = useMemo(
    () =>
      (selectedAddons || [])
        .map((addon) => addon.id)
        .sort()
        .join(','),
    [selectedAddons]
  );
  const totalDuration = useMemo(
    () => computeStorefrontTotalDurationMinutes(service, selectedPackage, selectedAddons),
    [service, selectedPackage, selectedAddons]
  );
  const monthKey = monthCursor ? format(startOfMonth(monthCursor), 'yyyy-MM') : '';
  const minDateKey = minDate ? format(startOfDay(minDate), 'yyyy-MM-dd') : '';
  const maxDateKey = maxDate ? format(startOfDay(maxDate), 'yyyy-MM-dd') : '';
  const companyHoursKey = useMemo(() => JSON.stringify(companyHours || {}), [companyHours]);
  const requestKey = buildAvailabilityRequestKey({
    companyId,
    serviceId,
    packageId,
    addonIdsKey,
    totalDuration,
    locationId,
    customerNoStaffMessage,
    monthKey,
    minDateKey,
    maxDateKey,
    companyHoursKey,
  });

  useEffect(() => {
    if (!enabled || !companyId || !serviceId || !monthCursor) {
      setAvailabilityByDate(new Map());
      setLoading(false);
      return undefined;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);

    buildStorefrontCalendarAvailabilityMap({
      companyData,
      service,
      selectedPackage,
      selectedAddons,
      companyHours,
      locationId,
      location,
      customerNoStaffMessage,
      monthCursor,
      minDate,
      maxDate,
    })
      .then((map) => {
        if (requestIdRef.current !== requestId) return;
        setAvailabilityByDate(map);
      })
      .catch(() => {
        if (requestIdRef.current !== requestId) return;
        setAvailabilityByDate(new Map());
      })
      .finally(() => {
        if (requestIdRef.current !== requestId) return;
        setLoading(false);
      });

    return undefined;
  }, [enabled, requestKey]);

  const isDateUnavailable = useCallback(
    (day) => {
      if (!day) return false;

      const normalized = startOfDay(day);
      const key = format(normalized, 'yyyy-MM-dd');

      if (!isStorefrontDayOpen(normalized, companyHours)) {
        return true;
      }

      if (!availabilityByDate.has(key)) {
        return false;
      }

      return availabilityByDate.get(key) === false;
    },
    [availabilityByDate, companyHours]
  );

  const hasAvailabilityForDate = useCallback(
    (day) => {
      if (!day) return false;
      return !isDateUnavailable(day);
    },
    [isDateUnavailable]
  );

  return {
    availabilityByDate,
    loading,
    isDateUnavailable,
    hasAvailabilityForDate,
  };
}

export function createInitialCalendarMonth(date) {
  return startOfMonth(date ? startOfDay(date) : new Date());
}
